import {
  UpcomingPaymentCategory,
  UpcomingPaymentsShipmentGroup,
  UpcomingPaymentStatus,
  UpcomingPaymentUrgency
} from '@/features/Finance';
import { formatValue } from '@/lib/formatters';

export const UPCOMING_PAYMENTS_SUMMARY_LIMIT = 3;

export interface UpcomingPaymentViewItem {
  amount: number;
  category: UpcomingPaymentCategory;
  date: string;
  description: string | null;
  document: string | null;
  id: string;
  shipmentId: string | null;
  shipmentNumber: string;
  status: UpcomingPaymentStatus;
  title: string | null;
  urgency: UpcomingPaymentUrgency;
}

export interface UpcomingPaymentViewGroup {
  hasRedUrgency: boolean;
  id: string;
  items: UpcomingPaymentViewItem[];
  shipmentId: string | null;
  shipmentNumber: string;
  totalAmount: number;
}

export type UpcomingPaymentViewTabValue = 'all' | 'expected' | 'overdue';

export const filterUpcomingPaymentGroupsByTab = (
  groups: UpcomingPaymentViewGroup[],
  activeTab: UpcomingPaymentViewTabValue
): UpcomingPaymentViewGroup[] => {
  if (activeTab === 'all') return groups;

  if (activeTab === 'overdue') {
    return groups.filter((group) => group.items.some((item) => item.status === 'OVERDUE'));
  }

  return groups.filter((group) => group.items.every((item) => item.status === 'EXPECTED'));
};

export const filterUpcomingPaymentsByTab = (
  items: UpcomingPaymentViewItem[],
  activeTab: UpcomingPaymentViewTabValue
): UpcomingPaymentViewItem[] => {
  if (activeTab === 'all') return items;

  const statusByTab: Record<Exclude<UpcomingPaymentViewTabValue, 'all'>, UpcomingPaymentStatus> = {
    expected: 'EXPECTED',
    overdue: 'OVERDUE'
  };

  return items.filter((item) => item.status === statusByTab[activeTab]);
};

export const getUpcomingPaymentsSummaryItems = (
  items: UpcomingPaymentViewItem[]
): UpcomingPaymentViewItem[] => {
  return items.slice(0, UPCOMING_PAYMENTS_SUMMARY_LIMIT);
};

export const getUpcomingPaymentsSummaryGroups = (
  groups: UpcomingPaymentViewGroup[]
): UpcomingPaymentViewGroup[] => {
  return groups.slice(0, UPCOMING_PAYMENTS_SUMMARY_LIMIT);
};

export const getUpcomingPaymentsTotalAmount = (items: UpcomingPaymentViewItem[]): number => {
  return items.reduce((totalAmount, item) => totalAmount + item.amount, 0);
};

export const mapUpcomingPaymentItemsToViewGroups = (
  items: UpcomingPaymentViewItem[]
): UpcomingPaymentViewGroup[] => {
  const groupsMap = new Map<string, UpcomingPaymentViewGroup>();

  items.forEach((item) => {
    const groupId = item.shipmentId ?? item.shipmentNumber;
    const currentGroup = groupsMap.get(groupId);

    if (currentGroup) {
      currentGroup.hasRedUrgency = currentGroup.hasRedUrgency || item.urgency === 'RED';
      currentGroup.items.push(item);
      currentGroup.totalAmount += item.amount;

      return;
    }

    groupsMap.set(groupId, {
      hasRedUrgency: item.urgency === 'RED',
      id: groupId,
      items: [item],
      shipmentId: item.shipmentId,
      shipmentNumber: item.shipmentNumber,
      totalAmount: item.amount
    });
  });

  return Array.from(groupsMap.values());
};

export const mapUpcomingPaymentsGroupsToItems = (
  groups: UpcomingPaymentsShipmentGroup[] | null | undefined
): UpcomingPaymentViewItem[] => {
  if (!groups?.length) return [];

  return groups.flatMap((group, groupIndex) => {
    if (!group.items?.length) return [];

    return group.items.map((item, itemIndex) => ({
      amount: item.amount,
      category: item.category,
      date: item.date,
      description: item.description,
      document: item.document,
      id: item.id ?? [
        group.shipmentId ?? group.shipmentNumber ?? 'shipment',
        item.document ?? item.date,
        groupIndex,
        itemIndex
      ].join('-'),
      shipmentId: group.shipmentId,
      shipmentNumber: formatValue({ value: group.shipmentNumber ?? item.document }),
      status: item.status,
      title: item.title,
      urgency: item.urgency
    }));
  });
};

export const mapUpcomingPaymentsGroupsToViewGroups = (
  groups: UpcomingPaymentsShipmentGroup[] | null | undefined
): UpcomingPaymentViewGroup[] => {
  if (!groups?.length) return [];

  return groups.reduce<UpcomingPaymentViewGroup[]>((acc, group, groupIndex) => {
    if (!group.items?.length) return acc;

    const items = group.items.map((item) => ({
      amount: item.amount,
      category: item.category,
      date: item.date,
      description: item.description,
      document: item.document,
      id: item.id,
      shipmentId: group.shipmentId,
      shipmentNumber: formatValue({ value: group.shipmentNumber ?? item.document }),
      status: item.status,
      title: item.title,
      urgency: item.urgency
    }));

    acc.push({
      hasRedUrgency: items.some((item) => item.urgency === 'RED'),
      id: group.shipmentId ?? group.shipmentNumber ?? `shipment-${groupIndex}`,
      items,
      shipmentId: group.shipmentId,
      shipmentNumber: formatValue({ value: group.shipmentNumber ?? group.items[0]?.document }),
      totalAmount: items.reduce((totalAmount, item) => totalAmount + item.amount, 0)
    });

    return acc;
  }, []);
};